import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faUser,
  faFileContract,
  faChevronRight,
  faBars,
  faChevronDown,
  faMapMarkerAlt,
  faCalendarAlt,
  faCheckCircle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [ongoingLoads, setOngoingLoads] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [pendingBids, setPendingBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [countries, setCountries] = useState([]);
  const [expandedExpenses, setExpandedExpenses] = useState({});

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Fetch ongoing loads (approved/assigned loads)
  const fetchOngoingLoads = async () => {
    try {
      const { data, error } = await supabase
        .from('loads')
        .select(
          `
          *,
          loading_tasks,
          unloading_tasks,
          accepted_driver
        `
        )
        .eq('status', 'assigned')
        .order('updated_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setOngoingLoads(data || []);
    } catch (error) {
      console.error('Error fetching ongoing loads:', error);
    }
  };

  // Fetch loads waiting for driver response (last 5)
  const fetchPendingDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('loads')
        .select(
          `
          *,
          accepted_driver
        `
        )
        .eq('status', 'waitingDriver')
        .order('updated_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPendingDrivers(data || []);
    } catch (error) {
      console.error('Error fetching loads waiting for driver response:', error);
    }
  };

  // Fetch pending and counter offer bids (last 5)
  const fetchPendingBids = async () => {
    try {
      const { data, error } = await supabase
        .from('bids')
        .select(
          `
          *,
          load:loads(formatted_id, customer_name, loading_country, discharge_country),
          driver_info
        `
        )
        .in('status', ['pending', 'countered'])
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setPendingBids(data || []);
    } catch (error) {
      console.error('Error fetching pending bids:', error);
    }
  };

  const fetchLoadExpenses = async () => {
    if (!ongoingLoads) return;
    const { data, error } = await supabase
      .from('load_expenses')
      .select('*')
      .in('load_id', ongoingLoads.map((load) => load.id))
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching expenses:', error);
      return [];
    }
    setExpenses(data || []);
  };

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase.from('countries').select('*');
      if (error) throw error;
      setCountries(data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOngoingLoads(),
        fetchPendingDrivers(),
        fetchPendingBids(),
        fetchCountries(),
      ]);
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (ongoingLoads.length > 0) {
      fetchLoadExpenses();
    }
  }, [ongoingLoads]);

  // Calculate progress for a load
  const calculateProgress = (load) => {
    const loadingTasks = load.loading_tasks || [];
    const unloadingTasks = load.unloading_tasks || [];
    const allTasks = [...loadingTasks, ...unloadingTasks];

    if (allTasks.length === 0) return 0;

    const completedTasks = allTasks.filter(
      (task) => task.status === 'completed'
    ).length;
    return Math.round((completedTasks / allTasks.length) * 100);
  };

  // Get progress for loading tasks
  const getLoadingProgress = (load) => {
    const loadingTasks = load.loading_tasks || [];
    if (loadingTasks.length === 0)
      return { completed: 0, total: 0, percentage: 0 };

    const completed = loadingTasks.filter(
      (task) => task.status === 'completed'
    ).length;
    const percentage = Math.round((completed / loadingTasks.length) * 100);

    return { completed, total: loadingTasks.length, percentage };
  };

  // Get progress for unloading tasks
  const getUnloadingProgress = (load) => {
    const unloadingTasks = load.unloading_tasks || [];
    if (unloadingTasks.length === 0)
      return { completed: 0, total: 0, percentage: 0 };

    const completed = unloadingTasks.filter(
      (task) => task.status === 'completed'
    ).length;
    const percentage = Math.round((completed / unloadingTasks.length) * 100);

    return { completed, total: unloadingTasks.length, percentage };
  };

  const toggleExpenses = (loadId) => {
    setExpandedExpenses((prev) => ({
      ...prev,
      [loadId]: !prev[loadId],
    }));
  };

  const getGroupedExpenses = (loadId) => {
    const loadExpenses = expenses.filter((exp) => exp.load_id === loadId);
    
    if (loadExpenses.length === 0) return {};

    const grouped = {};
    
    loadExpenses.forEach((expense) => {
      const countryData = countries.find((c) => c.id === expense.country);
      const countryName = countryData ? countryData.name_tr : 'Bilinmeyen';
      const currency = expense.currency_type;
      const key = `${countryName}-${currency}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          country: countryName,
          currency: currency,
          total: 0,
        };
      }
      
      grouped[key].total += expense.amount;
    });

    return grouped;
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button className="text-gray-500 lg:hidden">
                  <FontAwesomeIcon icon={faBars} className="text-xl" />
                </button>
              </div>
              <div className="relative">
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
                >
                  <span>{user?.email}</span>
                  <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
          <div className="container mx-auto px-6 py-8">
            <div className="flex items-center justify-center h-64">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-4xl text-blue-500 animate-spin"
              />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-2xl font-medium text-gray-800">Dashboard</h1>
            <p className="text-sm text-gray-500">
              Genel bakış ve son aktiviteler
            </p>
          </div>

          {/* Ongoing Transports - Full width */}
          <div className="mb-6">
            <div>
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faTruck}
                        className="text-blue-500 mr-2"
                      />
                      <h2 className="text-lg font-medium text-gray-800">
                        Devam Eden Taşımalar
                      </h2>
                    </div>
                    <button
                      onClick={() => navigate('/loads?tab=agreed')}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      Tümünü Gör
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="ml-1 text-xs"
                      />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {ongoingLoads.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      Devam eden taşıma bulunmuyor
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {ongoingLoads.map((load) => {
                        const loadingProgress = getLoadingProgress(load);
                        const unloadingProgress = getUnloadingProgress(load);
                        const overallProgress = calculateProgress(load);

                        return (
                          <div
                            key={load.id}
                            className="border border-gray-200 rounded-md p-3 hover:bg-gray-50"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center space-x-4">
                                <div>
                                  <h3 className="text-sm font-medium text-gray-900">
                                    {load.formatted_id}
                                  </h3>
                                  {load.reference_number && (
                                    <span className="text-xs text-gray-500">
                                      Ref: {load.reference_number}
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {load.customer_name}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {load.loading_country} →{' '}
                                  {load.discharge_country}
                                </div>
                                {load.accepted_driver && (
                                  <div className="text-xs text-gray-600">
                                    {load.accepted_driver.first_name}{' '}
                                    {load.accepted_driver.last_name}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold text-blue-600">
                                  {overallProgress}%
                                </div>
                              </div>
                            </div>

                            {/* Compact Progress bars */}
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                  <span>Yükleme</span>
                                  <span>
                                    {loadingProgress.completed}/
                                    {loadingProgress.total}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-green-500 h-1.5 rounded-full"
                                    style={{
                                      width: `${loadingProgress.percentage}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                                  <span>Boşaltma</span>
                                  <span>
                                    {unloadingProgress.completed}/
                                    {unloadingProgress.total}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-blue-500 h-1.5 rounded-full"
                                    style={{
                                      width: `${unloadingProgress.percentage}%`,
                                    }}
                                  ></div>
                                </div>
                              </div>
                            </div>

                            {/* Expenses Section */}
                            {expenses.filter((exp) => exp.load_id === load.id)
                              .length > 0 && (
                              <div className="mt-3 border-t border-gray-200 pt-3">
                                <button
                                  onClick={() => toggleExpenses(load.id)}
                                  className="flex items-center justify-between w-full text-left hover:bg-gray-50 px-2 py-1 rounded"
                                >
                                  <span className="text-xs font-medium text-gray-700">
                                    Masraflar
                                  </span>
                                  <FontAwesomeIcon
                                    icon={
                                      expandedExpenses[load.id]
                                        ? faChevronDown
                                        : faChevronRight
                                    }
                                    className="text-xs text-gray-400"
                                  />
                                </button>

                                {expandedExpenses[load.id] && (
                                  <div className="mt-2 space-y-1 px-2">
                                    {Object.values(
                                      getGroupedExpenses(load.id)
                                    )
                                      .sort((a, b) => a.country.localeCompare(b.country, 'tr'))
                                      .map((group, idx) => (
                                        <div
                                          key={idx}
                                          className="flex items-center justify-between text-xs"
                                        >
                                          <span className="text-gray-600">
                                            {group.country}:
                                          </span>
                                          <span className="font-semibold text-gray-900">
                                            {group.total.toLocaleString('tr-TR', {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            })}{' '}
                                            {group.currency}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom sections with equal width */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Loads Waiting for Driver Response */}
            <div>
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faUser}
                        className="text-green-500 mr-2"
                      />
                      <h2 className="text-lg font-medium text-gray-800">
                        Sürücü Cevabı Bekleyen
                      </h2>
                    </div>
                    <button
                      onClick={() => navigate('/loads?tab=driver')}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      Tümünü Gör
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="ml-1 text-xs"
                      />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {pendingDrivers.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      Sürücü cevabı bekleyen yük bulunmuyor
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {pendingDrivers.map((load) => (
                        <div
                          key={load.id}
                          className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {load.formatted_id}
                              </h3>
                              {load.reference_number && (
                                <p className="text-xs text-gray-500">
                                  Ref: {load.reference_number}
                                </p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {load.customer_name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {load.loading_country} →{' '}
                                {load.discharge_country}
                              </p>
                              {load.accepted_driver && (
                                <p className="text-xs text-gray-600 mt-1">
                                  Sürücü: {load.accepted_driver.first_name}{' '}
                                  {load.accepted_driver.last_name}
                                </p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(load.updated_at).toLocaleDateString(
                                  'tr-TR'
                                )}
                              </p>
                            </div>
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Cevap Bekliyor
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pending Bids */}
            <div>
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={faFileContract}
                        className="text-orange-500 mr-2"
                      />
                      <h2 className="text-lg font-medium text-gray-800">
                        Tekliflere Genel Bakış
                      </h2>
                    </div>
                    <button
                      onClick={() => navigate('/bids')}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      Tümünü Gör
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="ml-1 text-xs"
                      />
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  {pendingBids.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      Bekleyen teklif bulunmuyor
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {pendingBids.map((bid) => (
                        <div
                          key={bid.id}
                          className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center mb-1">
                                <h3 className="text-sm font-medium text-gray-900">
                                  {bid.load?.formatted_id || 'N/A'}
                                </h3>
                                <span
                                  className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                                    bid.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-blue-100 text-blue-800'
                                  }`}
                                >
                                  {bid.status === 'pending'
                                    ? 'Beklemede'
                                    : 'Karşı Teklif'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mb-1">
                                {bid.load?.customer_name} •{' '}
                                {bid.load?.loading_country} →{' '}
                                {bid.load?.discharge_country}
                              </p>
                              <p className="text-xs text-gray-600">
                                Teklif:{' '}
                                {bid.price
                                  ? `${bid.price} ${bid.currency || 'EUR'}`
                                  : 'Belirtilmemiş'}
                              </p>
                              {bid.driver_info && (
                                <p className="text-xs text-gray-500">
                                  {bid.driver_info.first_name}{' '}
                                  {bid.driver_info.last_name}
                                </p>
                              )}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(bid.created_at).toLocaleDateString(
                                'tr-TR'
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Dashboard;